// api/apiClient.ts
import { Method } from "axios"; // For type reference, assuming axios-like types

const baseUrl = process.env.REACT_APP_BASE_URL || "";

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
    statusCode?: number;
    statusMessage?: string;
    error?: unknown;
}

export const apiClient = {
    async request<T>(
        url: string,
        method: Method,
        body?: Record<string, any> | FormData,
        isFormData: boolean = false,
    ): Promise<ApiResponse<T>> {
        const options: RequestInit = {
            method,
            credentials: "include",
            headers: isFormData ? {} : { "Content-Type": "application/json", Accept: "application/json" },
            mode: "cors",
        };

        if (body) {
            if (isFormData && !(body instanceof FormData)) {
                const formData = new FormData();
                for (const key of Object.keys(body)) {
                    const value = body[key];
                    if (value === undefined || value === null) continue;
                    if (Array.isArray(value)) {
                        for (const item of value) {
                            formData.append(`${key}[]`, typeof item === "object" ? JSON.stringify(item) : item);
                        }
                    } else if (value instanceof File || value instanceof Blob) {
                        formData.append(key, value);
                    } else if (typeof value === "object") {
                        formData.append(key, JSON.stringify(value));
                    } else {
                        formData.append(key, value);
                    }
                }
                options.body = formData;
            } else {
                options.body = body instanceof FormData ? body : JSON.stringify(body);
            }
        }

        try {
            const response = await fetch(`${baseUrl}${url}`, options);
            if (!response.ok) {
            let errorMessage = `HTTP error ${response.status}`;

            try {
                const errorData = await response.json();
                errorMessage =
                errorData?.error?.message ||
                errorData?.message ||
                errorMessage;
            } catch (e) {
                // fallback if JSON parsing fails
            }

            throw new Error(errorMessage);
            }
            const data: ApiResponse<T> = await response.json();
            return data;
        } catch (error) {
            throw new Error(`API request failed: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    },

    get<T>(url: string): Promise<ApiResponse<T>> {
        return apiClient.request<T>(url, "GET");
    },

    post<T>(url: string, body?: Record<string, any> | FormData, isFormData: boolean = false): Promise<ApiResponse<T>> {
        return apiClient.request<T>(url, "POST", body, isFormData);
    },

    patch<T>(url: string, body?: Record<string, any> | FormData, isFormData: boolean = false): Promise<ApiResponse<T>> {
        return apiClient.request<T>(url, "PATCH", body, isFormData);
    },

    put<T>(url: string, body?: Record<string, any> | FormData, isFormData: boolean = false): Promise<ApiResponse<T>> {
        return apiClient.request<T>(url, "PUT", body, isFormData);
    },

    delete<T>(url: string, body?: Record<string, any> | FormData, isFormData: boolean = false): Promise<ApiResponse<T>> {
        return apiClient.request<T>(url, "DELETE", body, isFormData);
    },
};